import { useEffect, useState } from "react";
import { Eye, Pencil } from "lucide-react";
import "../../styles/settings/PromptManagement.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const PromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");

  const [addPrompt, setAddPrompt] = useState(null);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [viewPrompt, setViewPrompt] = useState(null);
  const [error, setError] = useState("");

  /* ================= FETCH PROMPTS ================= */
  const fetchPrompts = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/prompts`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setPrompts(data);
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  /* ================= TOGGLE STATUS ================= */
  const toggleStatus = async (prompt) => {
    await fetch(`${API_BASE}/admin/prompts/${prompt.prompt_id}/status`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        prompt_status: !prompt.prompt_status,
      }),
    });
    fetchPrompts();
  };

  /* ================= ADD PROMPT ================= */
  const handleAddChange = (e) => {
    const { name, value } = e.target;
    setAddPrompt((p) => ({ ...p, [name]: value }));
  };

  const saveAddPrompt = async () => {
    setError("");
    const res = await fetch(`${API_BASE}/admin/prompts`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        ...addPrompt,
        prompt_status: true,
      }),
    });

    if (!res.ok) {
      const data = await res.json();
      setError(data.message);
      return;
    }

    setAddPrompt(null);
    fetchPrompts();
  };

  /* ================= EDIT PROMPT ================= */
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingPrompt((p) => ({ ...p, [name]: value }));
  };

  const saveEditPrompt = async () => {
    setError("");
    const res = await fetch(
      `${API_BASE}/admin/prompts/${editingPrompt.prompt_id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          prompt_seq_no: editingPrompt.prompt_seq_no,
          prompts: editingPrompt.prompts,
          draft_section: editingPrompt.draft_section,
        }),
      }
    );

    if (!res.ok) {
      const data = await res.json();
      setError(data.message);
      return;
    }

    setEditingPrompt(null);
    fetchPrompts();
  };

  /* ================= FILTER ================= */
  const filteredPrompts = prompts.filter((p) => {
    if (statusFilter === "All") return true;
    if (statusFilter === "Active") return p.prompt_status === true;
    if (statusFilter === "Inactive") return p.prompt_status === false;
    return true;
  });

  if (loading) return <p>Loading prompts...</p>;

  return (
    <div className="settings-page">
      {/* HEADER */}
      <div className="settings-header">
        <h2>Prompt Management</h2>
        <button
          className="primary-btn"
          onClick={() =>
            setAddPrompt({ prompt_seq_no: "", prompts: "", draft_section: "" })
          }
        >
          + Add Prompt
        </button>
      </div>

      {/* FILTER */}
      <div className="filters">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {/* TABLE */}
      <table className="settings-table">
        <thead>
          <tr>
            <th>Seq</th>
            <th>Prompt</th>
            <th>Draft Section</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {filteredPrompts.map((p) => (
            <tr key={p.prompt_id}>
              <td>{p.prompt_seq_no}</td>
              <td className="truncate">{p.prompts}</td>
              <td>{p.draft_section}</td>

              {/* ✅ TOGGLE */}
              <td>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={p.prompt_status}
                    onChange={() => toggleStatus(p)}
                  />
                  <span className="slider"></span>
                </label>
              </td>

              <td className="actions">
                <button onClick={() => setViewPrompt(p)}>
                  <Eye size={16} />
                </button>
                <button onClick={() => setEditingPrompt(p)}>
                  <Pencil size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* ADD MODAL */}
      {addPrompt && (
        <Modal title="Add Prompt">
          <input name="prompt_seq_no" placeholder="Sequence" onChange={handleAddChange} />
		  <textarea
			  name="prompts"
			  placeholder="Prompt"
			  onChange={handleAddChange}
			  rows={5}
			  style={{ width: "100%", resize: "vertical" }}
			/>
          <input name="draft_section" placeholder="Draft Section" onChange={handleAddChange} />
          {error && <p className="error-text">{error}</p>}
          <div className="modal-actions">
            <button className="primary-btn" onClick={saveAddPrompt}>Save</button>
            <button className="secondary-btn" onClick={() => setAddPrompt(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* EDIT MODAL */}
      {editingPrompt && (
        <Modal title="Edit Prompt">
          <input name="prompt_seq_no" value={editingPrompt.prompt_seq_no} onChange={handleEditChange} />
		  <textarea
			  name="prompts"
			  value={editingPrompt.prompts}
			  onChange={handleEditChange}
			  rows={5}
			  style={{ width: "100%", resize: "vertical" }}
			/>
          <input name="draft_section" value={editingPrompt.draft_section} onChange={handleEditChange} />
          {error && <p className="error-text">{error}</p>}
          <div className="modal-actions">
            <button className="primary-btn" onClick={saveEditPrompt}>Save</button>
            <button className="secondary-btn" onClick={() => setEditingPrompt(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* VIEW MODAL */}
      {viewPrompt && (
        <Modal title="Prompt Details">
          <div className="prompt-view-grid">
            <div className="label">Seq</div><div className="value">{viewPrompt.prompt_seq_no}</div>
            <div className="label">Prompt</div><div className="value">{viewPrompt.prompts}</div>
            <div className="label">Draft</div><div className="value">{viewPrompt.draft_section}</div>
            <div className="label">Status</div>
<div
  className={`value ${
    viewPrompt.prompt_status ? "status-active" : "status-inactive"
  }`}
>
  {viewPrompt.prompt_status ? "Active" : "Inactive"}
</div>

            
          </div>
          <div className="modal-actions center">
            <button className="secondary-btn" onClick={() => setViewPrompt(null)}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
};

/* MODAL */
const Modal = ({ title, children }) => (
  <div className="modal-overlay">
    <div className="modal-card">
      <h2>{title}</h2>
      {children}
    </div>
  </div>
);

export default PromptManagement;
